<!-- src/components/dashboard/VenueKpiRow.vue -->
<template>
  <div class="venue-kpi-row">
    <div class="row">
      <div v-for="k in items" :key="k.key" class="col s12 m6 l3">
        <KpiMiniCard
          :label="k.label"
          :value="k.value"
          :sub-label="k.subLabel"
          :delta="k.delta"
          :formatter="k.formatter"
          :tone="k.tone || ''"
          :has-score="!!k.hasScore"
        />
      </div>
    </div>

    <div class="row">
      <div class="col s12">
        <div class="card venue-kpi-collapsible-card">
          <div class="card-content" style="padding: 0">
            <ul ref="tillsCollapsibleEl" class="collapsible expandable" style="margin: 0">
              <li>
                <div
                  class="collapsible-header venue-kpi-header"
                  style="display: block; padding: 14px 16px"
                >
                  <div class="row venue-kpi-metric-row" style="margin: 0">
                    <div class="col s12 m6 l6">
                      <div
                        class="venue-kpi-header-title"
                        style="display: flex; align-items: center; gap: 8px"
                      >
                        <i class="material-icons" style="line-height: 1">
                          point_of_sale
                        </i>

                        <div style="font-size: 20px; font-weight: 700">Weekly Tills</div>
                      </div>
                    </div>

                    <div class="col s12 m6 l6">
                      <div class="row venue-kpi-metric-row" style="margin: 0">
                        <div class="col s4 right-align">
                          <div class="grey-text text-darken-1" style="font-size: 12px">
                            Actual
                          </div>

                          <div style="font-weight: 900; font-size: 16px; margin-top: 2px">
                            {{ moneyGBP(tillsWeekActualTotal) }}
                          </div>
                        </div>

                        <div class="col s4 right-align">
                          <div class="grey-text text-darken-1" style="font-size: 12px">
                            Z-Read
                          </div>

                          <div style="font-weight: 900; font-size: 16px; margin-top: 2px">
                            {{ moneyGBP(tillsWeekZReadTotal) }}
                          </div>
                        </div>

                        <div class="col s4 right-align">
                          <div class="grey-text text-darken-1" style="font-size: 12px">
                            Variance
                          </div>

                          <div
                            style="font-weight: 900; font-size: 16px; margin-top: 2px"
                            :class="tillsVarianceClass(tillsWeekVariance)"
                          >
                            {{ tillsSignedMoneyGBP(tillsWeekVariance) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  class="collapsible-body venue-kpi-collapsible-body"
                  style="padding: 0 16px 16px 16px"
                >
                  <WeeklyTillsCard ref="tillsRef" />
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <br />

    <div class="row">
      <div class="col s12">
        <div class="card venue-kpi-collapsible-card">
          <div class="card-content" style="padding: 0">
            <ul
              ref="bankingCollapsibleEl"
              class="collapsible expandable"
              style="margin: 0"
            >
              <li>
                <div
                  class="collapsible-header venue-kpi-header"
                  style="display: block; padding: 14px 16px"
                >
                  <div class="row venue-kpi-metric-row" style="margin: 0">
                    <div class="col s12 m6 l6">
                      <div
                        class="venue-kpi-header-title"
                        style="display: flex; align-items: center; gap: 8px"
                      >
                        <i class="material-icons" style="line-height: 1"> payments </i>

                        <div style="font-size: 20px; font-weight: 700">
                          Banking Summary
                        </div>
                      </div>
                    </div>

                    <div class="col s12 m6 l6">
                      <div class="row venue-kpi-metric-row" style="margin: 0">
                        <div class="col s4 right-align">
                          <div class="grey-text text-darken-1" style="font-size: 12px">
                            Total PDQ
                          </div>

                          <div style="font-weight: 900; font-size: 16px; margin-top: 2px">
                            {{ moneyGBP(bankingPdqTotal) }}
                          </div>
                        </div>

                        <div class="col s4 right-align">
                          <div class="grey-text text-darken-1" style="font-size: 12px">
                            Cash
                          </div>

                          <div style="font-weight: 900; font-size: 16px; margin-top: 2px">
                            {{ moneyGBP(bankingCashTotal) }}
                          </div>
                        </div>

                        <div class="col s4 right-align">
                          <div class="grey-text text-darken-1" style="font-size: 12px">
                            Total
                          </div>

                          <div style="font-weight: 900; font-size: 16px; margin-top: 2px">
                            {{ moneyGBP(bankingGrandTotal) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  class="collapsible-body venue-kpi-collapsible-body"
                  style="padding: 0 16px 16px 16px"
                >
                  <BankingSummaryCard
                    ref="bankingRef"
                    :venue-id="venueId"
                    :week-ending-date="weekEndingISO"
                  />
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, nextTick } from "vue";

import M from "materialize-css";

import KpiMiniCard from "@/components/dashboard/KpiMiniCard.vue";
import WeeklyTillsCard from "@/components/dashboard/WeeklyTillsCard.vue";
import BankingSummaryCard from "@/components/dashboard/BankingSummaryCard.vue";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

defineProps({
  items: {
    type: Array,
    required: true,
  },
});

const { venueId, weekEnding, setBankingSummaryPayload } = useVenueContext();

const tillsCollapsibleEl = ref(null);

const bankingCollapsibleEl = ref(null);

const tillsRef = ref(null);

const bankingRef = ref(null);

const weekEndingISO = computed(() => {
  let raw = weekEnding?.value || "";

  if (!raw && typeof window !== "undefined") {
    raw = localStorage.getItem("weekEnding") || "";
  }

  if (!raw) {
    return "";
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }

  const d = new Date(raw);

  if (!Number.isNaN(d.getTime())) {
    return d.toISOString().slice(0, 10);
  }

  return String(raw).replace(/\//g, "-");
});

function moneyGBP(n) {
  const v = Math.round((Number(n) || 0) * 100) / 100;

  return v.toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const tillsWeekActualTotal = computed(
  () =>
    Number(
      tillsRef.value?.weekActualTotal?.value ?? tillsRef.value?.weekActualTotal ?? 0
    ) || 0
);

const tillsWeekZReadTotal = computed(
  () =>
    Number(
      tillsRef.value?.weekZReadTotal?.value ?? tillsRef.value?.weekZReadTotal ?? 0
    ) || 0
);

const tillsWeekVariance = computed(
  () =>
    Number(tillsRef.value?.weekVariance?.value ?? tillsRef.value?.weekVariance ?? 0) || 0
);

const tillsSignedMoneyGBP = (n) => {
  const fn = tillsRef.value?.signedMoneyGBP;

  return typeof fn === "function" ? fn(n) : moneyGBP(n);
};

const tillsVarianceClass = (n) => {
  const fn = tillsRef.value?.varianceClass;

  return typeof fn === "function" ? fn(n) : "";
};

const bankingPdqTotal = computed(() => {
  const t = bankingRef.value?.totals;

  const v = t?.value ?? t;

  return Number(v?.pdq ?? 0) || 0;
});

const bankingCashTotal = computed(() => {
  const t = bankingRef.value?.totals;

  const v = t?.value ?? t;

  return Number(v?.cash ?? 0) || 0;
});

const bankingGrandTotal = computed(() => bankingPdqTotal.value + bankingCashTotal.value);

function syncBankingPayloadToContext() {
  const payload = bankingRef.value?.bankingSummaryPayload?.value ?? null;

  setBankingSummaryPayload(payload);
}

onMounted(async () => {
  await nextTick();

  if (tillsCollapsibleEl.value) {
    M.Collapsible.init(tillsCollapsibleEl.value, {
      accordion: false,
    });
  }

  if (bankingCollapsibleEl.value) {
    M.Collapsible.init(bankingCollapsibleEl.value, {
      accordion: false,
    });
  }

  try {
    const tillsRefresh = tillsRef.value?.refresh;

    if (typeof tillsRefresh === "function") {
      await tillsRefresh();
    }
  } catch (e) {
    // ignore
  }

  try {
    const bankingRefresh = bankingRef.value?.refresh;

    if (typeof bankingRefresh === "function") {
      await bankingRefresh();
    }
  } catch (e) {
    // ignore
  }

  syncBankingPayloadToContext();
});
</script>

<style scoped>
.venue-kpi-row {
  width: 100%;
}

.venue-kpi-collapsible-card {
  overflow: hidden;
}

.venue-kpi-collapsible-body {
  padding: 0 16px 16px 16px;
}

@media (max-width: 600px) {
  .venue-kpi-header {
    padding: 12px !important;
  }

  .venue-kpi-header .row {
    display: block;
  }

  .venue-kpi-header-title {
    margin-bottom: 10px;
  }

  .venue-kpi-metric-row {
    display: grid !important;
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .venue-kpi-metric-row .col {
    width: 100% !important;
    text-align: left !important;
  }

  .venue-kpi-collapsible-body {
    padding: 0 12px 12px 12px !important;
  }
}
</style>
